/**
 * Read-Only Publisher
 *
 * The shared half of the read-only reaction. While an admin is signed in as a
 * member from the admin panel, the API refuses every write with `READ_ONLY`
 * (403). The request coordinator publishes every typed error here; this module
 * picks out those refusals, applies the restraint rules, and only then hands
 * one to the platform's registered `ReadOnlyPresenter`. Modelled on the
 * paywall publisher, whose rules it shares for the same reasons.
 *
 * Restraint rules, in order:
 *
 * 1. **No presenter, no work.** An unregistered platform behaves exactly as it
 *    did before the seam existed, and its pages keep their own error notices.
 * 2. **Deferred delivery.** Presentation waits for a macrotask, so the caller's
 *    `catch` has run first and had its chance to claim.
 * 3. **Claims win.** A request the app made on its own (marking a notification
 *    read as it arrives) calls `claimReadOnlyError` in its `catch`: the admin
 *    didn't cause it, so it is dropped quietly. Claims are keyed on the error
 *    INSTANCE, which `rethrowTyped` preserves.
 * 4. **One notice per burst.** A presentation inside the window of the last
 *    one is dropped: a form that saves in three requests says it once.
 *
 * Alongside, `wasReadOnlyRefusedRecently` lets the platform's own error toasts
 * stand down for the window, so the page's "Could not save…" never sits under
 * the server's words. It is stamped synchronously, at publish, because the
 * page's `catch` runs before any deferred work.
 *
 * Presenter failures are swallowed: presentation is a side effect of a request
 * failing and must never alter how the failure propagates to its caller.
 */

import { getReadOnlyPresenter } from '../../di/container';
import { isReadOnlyError } from '../../types/api/errors';
import { dayjs } from '../../utils/date';

import type { ApiErrorClass } from '../../types/api/errors';

/**
 * How long one refusal speaks for the ones around it: long enough for a
 * form's parallel requests and the page's own toast to land inside it.
 */
const BURST_WINDOW_MS = 1000;

/** Refusals a caller has claimed as its own quiet business. */
let claimedErrors = new WeakSet<ApiErrorClass>();

/** When a refusal was last published, for the page toasts' stand-down. */
let lastRefusedAt: number | null = null;

/** When a refusal was last presented, for the burst rule. */
let lastPresentedAt: number | null = null;

/** Invalidates presentations scheduled before a reset (test isolation). */
let generation = 0;

function within(at: number | null, now: number): boolean {
  return at !== null && now - at < BURST_WINDOW_MS;
}

/**
 * Publish a failed request's typed error toward the platform presenter.
 *
 * Called by the request coordinator for EVERY typed error it returns; anything
 * that is not a `READ_ONLY` refusal is ignored here, which keeps the call site
 * unconditional.
 */
export function publishReadOnly(error: unknown): void {
  if (!isReadOnlyError(error)) return;
  if (getReadOnlyPresenter() === undefined) return;

  lastRefusedAt = dayjs().valueOf();

  const scheduledGeneration = generation;
  setTimeout(() => {
    if (scheduledGeneration !== generation) return;
    if (claimedErrors.has(error)) return;

    const now = dayjs().valueOf();
    if (within(lastPresentedAt, now)) return;

    // Re-resolved at fire time: the presenter may have (un)registered since.
    const presenter = getReadOnlyPresenter();
    if (presenter === undefined) return;

    lastPresentedAt = now;
    try {
      presenter.present(error);
    } catch {
      // Presenting must never break the failure path it rides on.
    }
  }, 0);
}

/**
 * Mark a refusal as the caller's own, to be dropped quietly — for requests the
 * app makes without the user asking. Safe to call with any error: anything
 * that is not a `READ_ONLY` refusal is ignored, so it can be a `catch` handler
 * as it stands (`.catch(claimReadOnlyError)`).
 */
export function claimReadOnlyError(error: unknown): void {
  if (!isReadOnlyError(error)) return;
  claimedErrors.add(error);
}

/**
 * Whether a request was refused as read-only within the burst window. The
 * platform's error toasts read it to stand down while the presenter speaks.
 */
export function wasReadOnlyRefusedRecently(): boolean {
  return within(lastRefusedAt, dayjs().valueOf());
}

/**
 * Reset all publisher state AND invalidate any scheduled presentation. Test
 * isolation only.
 */
export function resetReadOnlyPublisher(): void {
  generation += 1;
  claimedErrors = new WeakSet<ApiErrorClass>();
  lastRefusedAt = null;
  lastPresentedAt = null;
}
